import {
    SpaceBetween,
    BreadcrumbGroup,
    Header,
    Box,
    Table,
    Button,
    TextFilter,
    Pagination,
    CollectionPreferences,
    Flashbar,
    Modal,
} from '@amzn/awsui-components-console';
import React, { useContext, useEffect, useState } from 'react';
import { deleteDeviceConfig, fetchDeviceConfig } from './apis';
import * as Constants from './constants';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { EmptyState, getFilterCounterText } from './utils';
import { RegionContext } from '../../CustomContext';
import * as GlobalConstants from '../../lib/constants';

/**
 * Device configuration component
 */
export default function DeviceConfigManager() {
    const [selectedDevice, setSelectedDevice] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [deviceConfigs, setDeviceConfig] = React.useState([]);
    const { selectedRegion, setSelectedRegion } = useContext(RegionContext);
    const [deleteDisabled, setDeleteDisabled] = React.useState(true);
    const [preferences, setPreferences] = useState({
        pageSize: 20,
        stickyColumns: { first: 1 },
        visibleContent: Constants.DEVICE_CONFIG_COLUMNS,
    });
    const { items, actions, collectionProps, filteredItemsCount, filterProps, paginationProps } = useCollection(
        deviceConfigs,
        {
            filtering: {
                noMatch: (
                    <EmptyState
                        title="No matches"
                        subtitle="We can’t find a match."
                        action={<Button onClick={() => actions.setFiltering('')}>Clear filter</Button>}
                    />
                ),
            },
            pagination: { pageSize: preferences.pageSize },
            sorting: {
                defaultState: { sortingColumn: Constants.SORTED_FILED, isDescending: true },
            },
            selection: {},
        }
    );
    const [successMessage, setSuccessMessage] = React.useState([]);
    const [errorMessage, setErrorMessage] = React.useState([]);

    /**
     * process selection change event
     */
    const onSelectionChange = (selectedItems) => {
        setSelectedDevice(selectedItems);
        setDeleteDisabled(false);
    };
    const startLoading = () => {
        // initialize loading status
        setLoading(true);
        setDeleteDisabled(true);
    };
    const endLoading = () => {
        // end loading status
        setLoading(false);
    };

    const displayErrorContent = (content) => {
        // display error message
        setErrorMessage([
            {
                type: 'error',
                dismissible: true,
                dismissLabel: 'Dismiss message',
                onDismiss: () => setErrorMessage([]),
                content: <>{content}</>,
                action: (
                    <Button href={GlobalConstants.CONTACT_US} iconAlign="right" target="_blank">
                        Contact us
                    </Button>
                ),
            },
        ]);
    };
    const displaySuccessContent = (content) => {
        // display success message
        setSuccessMessage([
            {
                type: 'success',
                dismissible: true,
                dismissLabel: 'Dismiss message',
                onDismiss: () => setSuccessMessage([]),
                content: <>{content}</>,
            },
        ]);
    };

    /**
     * load device configs table
     */
    const loadTable = () => {
        (async function () {
            startLoading();
            try {
                const response = await fetchDeviceConfig(selectedRegion.value);
                const deviceConfigs = response['configs'];
                setDeviceConfig(deviceConfigs);
            } catch (e) {
                console.error('Fail to load device configuration with error', e);
            }
            endLoading();
        })();
    };

    const onSubmitDelete = () => {
        // when delete button is triggered and deletion is confirmed
        (async function () {
            try {
                const dsn = selectedDevice[0]['dsn'];
                const labId = selectedDevice[0]['labId'];
                await deleteDeviceConfig(dsn, labId, selectedRegion.value);
                loadTable();
                displaySuccessContent(`Successfully deleted the configuration for device ${dsn}`);
            } catch (e) {
                console.error('Fail to delete configuration for selected device with error', e);
                displayErrorContent(
                    'Cannot delete device configuration. Contact our dev team @ds2-device-farm-dev for more details.'
                );
            }
        })();
    };

    useEffect(() => {
        // initialize the component
        loadTable();
    }, []);

    return (
        <SpaceBetween size="s">
            <BreadcrumbGroup
                items={[
                    { text: 'DS2 Device Farm', href: '#' },
                    { text: 'Device config management', href: '#/lab-management/device-configs' },
                ]}
                ariaLabel="Breadcrumbs"
            />
            <Header
                variant="h1"
                description="This page shows all device configurations that you are allowed to view and edit."
            >
                Welcome to DS2 Device Farm - Device config management
            </Header>
            <Table
                {...collectionProps}
                onSelectionChange={({ detail }) => onSelectionChange(detail.selectedItems)}
                selectedItems={selectedDevice}
                selectionType="single"
                header={
                    <Header
                        counter={`(${deviceConfigs.length})`}
                        actions={
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button onClick={loadTable} iconName="refresh" variant="icon" />
                                <Button disabled={deleteDisabled} onClick={onSubmitDelete}>
                                    Delete
                                </Button>
                            </SpaceBetween>
                        }
                    >
                        DDF Device Configurations
                    </Header>
                }
                columnDefinitions={Constants.DEVICE_CONFIG_COLUMNS_DEFINITION}
                visibleColumns={preferences.visibleContent}
                items={items}
                pagination={<Pagination {...paginationProps} />}
                filter={<TextFilter {...filterProps} countText={getFilterCounterText(filteredItemsCount)} />}
                empty={
                    <Box textAlign="center" color="inherit">
                        <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                            No device configuration to display
                        </Box>
                    </Box>
                }
                loading={loading}
                loadingText="Loading device configuration"
                stickyColumns={preferences.stickyColumns}
                resizableColumns
                preferences={
                    <CollectionPreferences
                        {...Constants.COLLECTION_PREFERENCES_PROPS}
                        preferences={preferences}
                        onConfirm={({ detail }) => setPreferences(detail)}
                    />
                }
            />
            <Flashbar items={errorMessage} />
            <Flashbar items={successMessage} />
        </SpaceBetween>
    );
}
